// Sugalarm — alternate sign-in methods (cycle 152): Continue with
// Google, Continue with Apple, Sign in with a passkey.
//
// Renders ONLY the methods `GET /api/auth/providers` reports available
// AND the runtime supports (window.PublicKeyCredential + an origin the
// RP covers, for passkeys). Renders nothing on the kiosk variant — the
// kiosk authenticates via device-token pairing, never a social account.
// Every success hands the server's TokenPair to the caller, which feeds
// useAuth.adoptSession — the one session path.
//
// v0.148.1 hid everything on the native Capacitor shell (Google
// disallows OAuth inside embedded webviews, Apple's JS popup can't
// complete there, no WebAuthn in WKWebView). Cycle 154 re-enables
// Google + Apple on NATIVE iOS through @capgo/capacitor-social-login —
// the real OS account picker / Apple sheet, never a webview — feeding
// the same /api/auth/oauth/* endpoints and adoptSession path. Native
// Android stays hidden (next cycle), passkeys stay web-only.
//
// SDK loading is lazy: the Google GIS / Apple JS scripts are injected
// only after the providers probe says the method is live, so an
// unconfigured deployment ships zero third-party bytes.

import { useCallback, useEffect, useRef, useState } from "react";

import * as authApi from "../../api/auth";
import type { AuthResponse, ProvidersOut } from "../../api/auth";
import { useFrameVariant } from "../../hooks/useFrameVariant";
import { friendlyAuthError } from "../../lib/authErrors";
import { isCapacitorNative } from "../../lib/capacitor";
import {
  isUserCancelled,
  nativeGoogleReady,
  nativeLogin,
  nativeSocialAvailable,
} from "../../lib/socialLogin";
import {
  decodeRequestOptions,
  encodeAssertion,
  originMatchesRpId,
  supportsPasskeys,
} from "../../lib/webauthn";

interface Props {
  onSuccess: (data: AuthResponse) => void;
  onError: (message: string) => void;
}

const GIS_SRC = "https://accounts.google.com/gsi/client";
const APPLE_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    // Keep the message human — it surfaces verbatim in the form's
    // error slot (v0.148.1; the raw URL used to leak through).
    s.onerror = () =>
      reject(
        new Error(
          "Sign-in service didn’t load — check your connection and try again.",
        ),
      );
    document.head.appendChild(s);
  });
}

const methodBtnStyle: React.CSSProperties = {
  appearance: "none",
  border: "0.5px solid var(--line-strong)",
  background: "var(--bg-sunk)",
  color: "var(--ink)",
  borderRadius: 10,
  padding: "12px 16px",
  fontWeight: 600,
  fontSize: 13,
  minHeight: 44,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
};

export function AuthMethods({ onSuccess, onError }: Props) {
  const variant = useFrameVariant();
  const [providers, setProviders] = useState<ProvidersOut | null>(null);
  const [busy, setBusy] = useState<"google" | "apple" | "passkey" | null>(
    null,
  );
  const googleReady = useRef(false);

  // Three surfaces (cycle 154):
  //   kiosk          → nothing (device-token pairing only)
  //   native shell   → iOS: native Google/Apple via the plugin;
  //                    Android: nothing (next cycle)
  //   web / PWA      → the JS-SDK stack below
  const native = isCapacitorNative();
  const nativeIos = native && nativeSocialAvailable();
  const suppressed = variant === "kiosk" || (native && !nativeIos);

  useEffect(() => {
    if (suppressed) return;
    void authApi.providers().then((r) => {
      if (r.ok) setProviders(r.data);
    });
  }, [suppressed]);

  // Cycle 154: native iOS — the OS-level flows. Declared before the
  // early returns so hook order stays stable.
  const doNative = useCallback(
    async (provider: "google" | "apple") => {
      if (busy) return;
      setBusy(provider);
      try {
        const { token, displayName } = await nativeLogin(
          provider,
          providers?.google_client_id ?? null,
        );
        // Google's ID token already carries the name claim; Apple sends
        // the name ONLY on first authorization — pass it along then.
        const r =
          provider === "google"
            ? await authApi.oauthGoogle(token)
            : await authApi.oauthApple(token, displayName ?? undefined);
        setBusy(null);
        if (r.ok) onSuccess(r.data);
        else
          onError(
            friendlyAuthError(
              r.error.message ||
                `${provider === "google" ? "Google" : "Apple"} sign-in failed`,
            ),
          );
      } catch (e) {
        setBusy(null);
        if (!isUserCancelled(e)) {
          onError(
            `${provider === "google" ? "Google" : "Apple"} sign-in didn’t complete — please try again.`,
          );
        }
      }
    },
    [busy, providers, onSuccess, onError],
  );

  const passkeyAvailable =
    providers?.passkey === true &&
    supportsPasskeys() &&
    originMatchesRpId(providers.rp_id);
  const googleAvailable =
    providers?.google === true && !!providers.google_client_id;
  const appleAvailable =
    providers?.apple === true && !!providers.apple_client_id;

  const doGoogle = useCallback(async () => {
    if (!providers?.google_client_id || busy) return;
    setBusy("google");
    try {
      await loadScript(GIS_SRC);
      const gsi = (
        window as unknown as {
          google?: {
            accounts: {
              id: {
                initialize: (cfg: unknown) => void;
                prompt: () => void;
              };
            };
          };
        }
      ).google;
      if (!gsi) throw new Error("Google sign-in failed to load");
      // GIS hands the ID token to the callback; the One Tap / button
      // prompt drives the UI.
      if (!googleReady.current) {
        gsi.accounts.id.initialize({
          client_id: providers.google_client_id,
          callback: async (resp: { credential: string }) => {
            const r = await authApi.oauthGoogle(resp.credential);
            setBusy(null);
            if (r.ok) onSuccess(r.data);
            else onError(friendlyAuthError(r.error.message || "Google sign-in failed"));
          },
          use_fedcm_for_prompt: true,
        });
        googleReady.current = true;
      }
      gsi.accounts.id.prompt();
      // The prompt is async + dismissible; release the spinner shortly
      // so a dismissed prompt doesn't wedge the button.
      setTimeout(() => setBusy((b) => (b === "google" ? null : b)), 4000);
    } catch (e) {
      setBusy(null);
      onError(e instanceof Error ? e.message : "Google sign-in failed");
    }
  }, [providers, busy, onSuccess, onError]);

  const doApple = useCallback(async () => {
    if (!providers?.apple_client_id || busy) return;
    setBusy("apple");
    try {
      await loadScript(APPLE_SRC);
      const AppleID = (
        window as unknown as {
          AppleID?: {
            auth: {
              init: (cfg: unknown) => void;
              signIn: () => Promise<{
                authorization: { id_token: string };
                user?: { name?: { firstName?: string; lastName?: string } };
              }>;
            };
          };
        }
      ).AppleID;
      if (!AppleID) throw new Error("Apple sign-in failed to load");
      AppleID.auth.init({
        clientId: providers.apple_client_id,
        scope: "name email",
        redirectURI: `${window.location.origin}/login`,
        usePopup: true,
      });
      const resp = await AppleID.auth.signIn();
      // Apple sends the name only on FIRST authorization, outside the
      // identity token — pass it along for the first link.
      const nameParts = [
        resp.user?.name?.firstName,
        resp.user?.name?.lastName,
      ].filter(Boolean);
      const r = await authApi.oauthApple(
        resp.authorization.id_token,
        nameParts.length ? nameParts.join(" ") : undefined,
      );
      setBusy(null);
      if (r.ok) onSuccess(r.data);
      else onError(r.error.message || "Apple sign-in failed");
    } catch (e) {
      setBusy(null);
      // A user-cancelled popup is not an error worth shouting about.
      const msg = e instanceof Error ? e.message : String(e);
      if (!/popup_closed|user_cancel/i.test(msg)) {
        onError("Apple sign-in failed");
      }
    }
  }, [providers, busy, onSuccess, onError]);

  const doPasskey = useCallback(async () => {
    if (busy) return;
    setBusy("passkey");
    try {
      const begin = await authApi.passkeyAuthBegin();
      if (!begin.ok) {
        throw new Error(begin.error.message || "Passkey sign-in unavailable");
      }
      const cred = (await navigator.credentials.get({
        publicKey: decodeRequestOptions(begin.data.options),
      })) as PublicKeyCredential | null;
      if (!cred) throw new Error("No passkey selected");
      const r = await authApi.passkeyAuthFinish(
        begin.data.challenge_id,
        encodeAssertion(cred),
      );
      setBusy(null);
      if (r.ok) onSuccess(r.data);
      else onError(r.error.message || "Passkey sign-in failed");
    } catch (e) {
      setBusy(null);
      const msg = e instanceof Error ? e.name : "";
      // NotAllowedError = the user dismissed the platform sheet.
      if (msg !== "NotAllowedError") {
        onError("Passkey sign-in failed");
      }
    }
  }, [busy, onSuccess, onError]);

  // ─── render (every hook above is unconditional) ────────────────────

  if (suppressed) return null;

  if (nativeIos) {
    const googleNative = providers?.google === true && nativeGoogleReady();
    const appleNative = providers?.apple === true;
    if (!googleNative && !appleNative) return null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {googleNative && (
          <button
            type="button"
            style={methodBtnStyle}
            onClick={() => void doNative("google")}
          >
            {busy === "google" ? "Working…" : "Continue with Google"}
          </button>
        )}
        {appleNative && (
          <button
            type="button"
            style={methodBtnStyle}
            onClick={() => void doNative("apple")}
          >
            {busy === "apple" ? "Working…" : "Continue with Apple"}
          </button>
        )}
      </div>
    );
  }

  if (!googleAvailable && !appleAvailable && !passkeyAvailable) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {googleAvailable && (
        <button type="button" style={methodBtnStyle} onClick={() => void doGoogle()}>
          {busy === "google" ? "Working…" : "Continue with Google"}
        </button>
      )}
      {appleAvailable && (
        <button type="button" style={methodBtnStyle} onClick={() => void doApple()}>
          {busy === "apple" ? "Working…" : "Continue with Apple"}
        </button>
      )}
      {passkeyAvailable && (
        <button type="button" style={methodBtnStyle} onClick={() => void doPasskey()}>
          {busy === "passkey" ? "Working…" : "Sign in with a passkey"}
        </button>
      )}
    </div>
  );
}
